import { Just } from "purify-ts";
import { match, P } from "ts-pattern";

import { createErrorHandler } from "@/app-utils/errors.ts";
import { type IConfiguredPlaybook, isNgsDataFileAttribute } from "@/app-utils/playbook.ts";
import { extractSamplesEditingInput } from "@/app-utils/sample.ts";
import type { Dictionary } from "@/app-utils/type.ts";
import {
  type Attribute,
  type GenomeViewerUiContext,
  type InputActivityBasicParameterInput,
  type InputEditSamplesInput,
  type InputNfParamInput,
  type InputPlaybookActivityInput,
  type InputPlaybookEventInput,
  type InputTableQuerySpec,
  type NfParamValue,
  type NfSampleAttribute,
  type Organism,
  type P2PlaybookConfig,
  type Playbook,
  type PlaybookEvent,
  type Project,
  type Region,
  UiContextEnum,
  type WorkflowExtendedConfig,
} from "@/graphql.ts";
import { NfParamTypeEnum } from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import { isFileAccessible } from "@/modules/files/utils/file.js";
import { extractValue } from "@/modules/workflow/utils/generateWorkflow.ts";
import type {
  Activity,
  WorkflowActivityParameterFieldProps,
  WorkflowErrorParameter,
  WorkflowParameter,
} from "@/modules/workflow/utils/types.ts";
import { generateValidationSchema } from "@/modules/workflow/utils/validationSchema.ts";
import type { IGenomeBrowserState } from "@/redux/modules/genome-browser.ts";
import {
  type ActivityInstanceExtendedParameterWithActivity,
  buildFieldProps,
  buildWorkflowParameter,
  flattenStagedActivityExtendedInstances,
} from "@/redux/modules/playbook-admin.ts";
import { PLAYBOOK_CLASS, PLAYBOOK_TYPE } from "@/settings/settings.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { SelMessage } from "../../messages/SelMessage.js";
import { notify } from "../../modules/notifications/notify.js";
import {
  createNfParamValue,
  createNfWorkflowParamValueFromEventInput,
  extractNfWorkflowParamInputValues,
} from "../../ui-modules/playbooks/ngs/NfWorkflowParamInputField.js";
import {
  CLOSE_NF_PLAYBOOK,
  EXECUTE_ANALYSIS_FAILURE,
  EXECUTE_ANALYSIS_REQUEST,
  EXECUTE_ANALYSIS_SUCCESS,
  LOAD_NF_PLAYBOOK_FAILURE,
  LOAD_NF_PLAYBOOK_REQUEST,
  LOAD_NF_PLAYBOOK_SUCCESS,
  OPEN_NF_PLAYBOOK,
  RESET_NF_PLAYBOOK_EXECUTION_STATE,
  UPDATE_NF_PLAYBOOK_EXECUTION_STATE,
} from "../action-types.js";
import type { IAppState } from "../reducer.js";
import { createReducer, type ReduxDispatch } from "../redux-utils.js";
import { auditPlaybookOpen, auditPlaybookSubmit, refreshEventsListing } from "./playbook-execution.js";
import type { ISampleEditingState } from "./sample-editing.js";

/**
 * State definition
 */
export interface INFPlaybookExecutionState {
  selectedPlaybookId: number; // playbook to open for execution
  playbook: Playbook; //  full version for execution or display/editing
  p2Activities: Activity[]; // needed for building the set of p2 parameter inputs
  // the following contains both the parameters and their current values (in workflowParameter) in a composite object
  p2Params: ActivityInstanceExtendedParameterWithActivity[]; // should later replace p2Activities AND p2ParameterValues
  initialPlaybookEventId: number;

  // execution payload
  project: Project;
  workflowValues: Dictionary<NfParamValue[]>;
  p2ParameterValues: WorkflowParameter[];
  eventName: string;
  eventDescription: string;
  reportTableQuerySpec: InputTableQuerySpec | null;
  sampleIds: number[];
  contentUnavailableWarning: string;
}

const initialState: INFPlaybookExecutionState = {
  selectedPlaybookId: null,
  playbook: null,
  initialPlaybookEventId: null,
  project: null,
  p2Activities: [],
  p2Params: [],
  workflowValues: {},
  p2ParameterValues: [],
  eventName: null,
  eventDescription: null,
  sampleIds: null,
  contentUnavailableWarning: null,
  reportTableQuerySpec: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [OPEN_NF_PLAYBOOK]: (state: INFPlaybookExecutionState, playbookId: number): INFPlaybookExecutionState => {
    return { ...state, selectedPlaybookId: playbookId };
  },
  [CLOSE_NF_PLAYBOOK]: (state: INFPlaybookExecutionState): INFPlaybookExecutionState => {
    return { ...state, selectedPlaybookId: null };
  },
  [RESET_NF_PLAYBOOK_EXECUTION_STATE]: (): INFPlaybookExecutionState => {
    return { ...initialState };
  },

  [LOAD_NF_PLAYBOOK_REQUEST]: (state: INFPlaybookExecutionState): INFPlaybookExecutionState => {
    return { ...state, playbook: null };
  },
  [LOAD_NF_PLAYBOOK_SUCCESS]: (
    state: INFPlaybookExecutionState,
    payload: { playbook: Playbook; p2Params: ActivityInstanceExtendedParameterWithActivity[] },
  ): INFPlaybookExecutionState => {
    return {
      ...state,
      playbook: payload.playbook,
      p2Params: payload.p2Params,
    };
  },
  [LOAD_NF_PLAYBOOK_FAILURE]: (state: INFPlaybookExecutionState): INFPlaybookExecutionState => {
    return { ...state, playbook: null };
  },

  [UPDATE_NF_PLAYBOOK_EXECUTION_STATE]: (
    state: INFPlaybookExecutionState,
    payload: Partial<INFPlaybookExecutionState>,
  ): INFPlaybookExecutionState => {
    return {
      ...state,
      ...payload,
    };
  },
});

/**
 * Action creators
 */
export function clearNfPlaybookExecutionData() {
  return {
    type: RESET_NF_PLAYBOOK_EXECUTION_STATE,
  };
}

export function updateNfPlaybookExecutionState(state: Partial<INFPlaybookExecutionState>) {
  return {
    type: UPDATE_NF_PLAYBOOK_EXECUTION_STATE,
    payload: state,
  };
}

export function openNFPlaybookForExecution(playbookId: number) {
  return {
    type: OPEN_NF_PLAYBOOK,
    payload: playbookId,
  };
}

export function closeNFPlaybookForExecution() {
  return {
    type: CLOSE_NF_PLAYBOOK,
  };
}

export function loadNfPlaybook(playbookId: number) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<Playbook> => {
    dispatch({ type: LOAD_NF_PLAYBOOK_REQUEST });

    return webApiClient
      .fetchNfPlaybook(playbookId)
      .then(async (playbook: Playbook) => {
        dispatch(auditPlaybookOpen(playbook));

        // if this is a P2 playbook, create the flattened list of parameters right away
        let p2Params: ActivityInstanceExtendedParameterWithActivity[] = [];
        if (playbook.p2Config) {
          // the workflow may be missing, e.g. if an activity of the workflow does not exist any more
          if (playbook.p2Config.workflow) {
            p2Params = await flattenStagedActivityExtendedInstances(
              playbook.p2Config.workflow?.extendedConfig.activities,
              true,
              false,
            );
          }
        }
        dispatch({
          type: LOAD_NF_PLAYBOOK_SUCCESS,
          payload: {
            playbook: playbook,
            // sort the parameters right away by the defined order
            p2Params: p2Params.sort((a, b) => ((a.sortOrder || 0) > (b.sortOrder || 0) ? 1 : -1)),
          },
        });

        return playbook;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_NF_PLAYBOOK_FAILURE,
          throwError: true,
          showAlert: false,
        }),
      );
  };
}
export function fetchEventDataForRerun(eventId: number) {
  return (dispatch, _getState: () => IAppState, webApiClient): Promise<PlaybookEvent> => {
    return webApiClient.fetchEventForRerun(eventId).catch(
      createErrorHandler(dispatch, {
        alertTitle: `Fetching of Event ${eventId} Failed`,
      }),
    );
  };
}

export function initNfPlaybookExecutionState() {
  return (dispatch: ReduxDispatch, getState: () => IAppState, _webApiClient: WebApiClient): Promise<void> => {
    const initialPlaybookEventId = getState().nfPlaybookExecution.initialPlaybookEventId;
    const playbook = getState().nfPlaybookExecution.playbook;
    const sampleIds = getState().nfPlaybookExecution.sampleIds;
    const genomeBrowser = getState().genomeBrowser;

    const defaultWorkflowValues: Dictionary<NfParamValue[]> = {};
    let contentWarning: string = null;
    if (initialPlaybookEventId) {
      return dispatch(fetchEventDataForRerun(initialPlaybookEventId)).then((playbookEvent) => {
        if (playbook.nfConfig) {
          const executionState: Partial<INFPlaybookExecutionState> =
            initNfPlaybookExecutionFormStateFromEvent(playbookEvent);
          dispatch(updateNfPlaybookExecutionState(executionState));
        } else if (playbook.p2Config) {
          initNfPlaybookExecutionFormStateFromProcessor(playbookEvent.workflowP2ExtendedConfig, sampleIds).then(
            (executionState: Partial<INFPlaybookExecutionState>) => {
              dispatch(updateNfPlaybookExecutionState(executionState));
            },
          );
        }
      });
    } else {
      if (playbook.nfConfig) {
        // init with default values, but make sure the default resources are not restricted
        for (const workflowParam of playbook.nfConfig.workflowParameters) {
          defaultWorkflowValues[workflowParam.identifier] = workflowParam.values.filter((nfParamValue) => {
            if (nfParamValue.file && !isFileAccessible(nfParamValue.file)) {
              contentWarning = selMessage({
                id: "playbook.execution.warning.default.fileResourcesUnavailable",
              });
              return false;
            }
            if (nfParamValue.organism && nfParamValue.organism.hidden) {
              contentWarning = selMessage({
                id: "playbook.execution.warning.default.genomeResourcesUnavailable",
              });
              return false;
            }
            return true;
          });
        }
        const executionState: Partial<INFPlaybookExecutionState> = {
          workflowValues: defaultWorkflowValues,
          contentUnavailableWarning: contentWarning,
        };
        dispatch(updateNfPlaybookExecutionState(executionState));
      } else if (playbook.p2Config) {
        return initNfPlaybookExecutionFormStateFromProcessor(
          playbook.p2Config.workflow?.extendedConfig,
          sampleIds,
        ).then((executionState: Partial<INFPlaybookExecutionState>) => {
          // init from genome viewer
          let completedExecutionState = { ...executionState };
          // init from genome browser e.g. for range analysis
          if (genomeBrowser.contig) {
            completedExecutionState = completeP2WorkflowValuesFromGenomeBrowserState(
              executionState,
              playbook.p2Config,
              genomeBrowser,
            );
          }
          dispatch(updateNfPlaybookExecutionState(completedExecutionState));
        });
      }

      return Promise.resolve();
    }
  };
}

export function completeP2WorkflowValuesFromGenomeBrowserState(
  executionState: Partial<INFPlaybookExecutionState>,
  p2Config: P2PlaybookConfig,
  genomeBrowser: IGenomeBrowserState,
): Partial<INFPlaybookExecutionState> {
  const uiContexts = p2Config.contexts;
  const genomeViewerContext: GenomeViewerUiContext = uiContexts?.find(
    (c) => c?.context === UiContextEnum.GenomeViewer,
  ) as GenomeViewerUiContext;

  // build a new execution state to enrich with the genome and region from the genome viewer
  const newExecutionState: Partial<INFPlaybookExecutionState> = {
    ...executionState,
  };

  // retrieve the relevant values from the genome viewer state
  const genome: Organism = genomeBrowser.genome;
  const region: Partial<Region> = genomeBrowser.contig && {
    contig: { ...genomeBrowser.contig, organism: genomeBrowser.genome },
    range: genomeBrowser.selectedRange || genomeBrowser.range,
  };

  const genomeParam = newExecutionState.p2Params?.find(
    (p) =>
      p.activity.identifier === genomeViewerContext.genomeParam.instanceId &&
      p.parameterDefinition.id === genomeViewerContext.genomeParam.parameterId,
  );
  if (genomeParam) {
    // create and set the genome param
    const genomeFieldProps: WorkflowActivityParameterFieldProps = buildFieldProps(genomeParam);
    const genomeValue = Just(Number(genome.id));
    const storedGenomeParam: WorkflowParameter = buildWorkflowParameter(
      genomeViewerContext.genomeParam.instanceId,
      genomeViewerContext.genomeParam.parameterId,
      "GenomeParam",
      genomeValue,
      true,
      generateValidationSchema(genomeFieldProps),
    );

    genomeParam.workflowParameter = storedGenomeParam;
  }

  // create and set the region param
  const regionParam = newExecutionState.p2Params?.find(
    (p) =>
      p.activity.identifier === genomeViewerContext.regionParam.instanceId &&
      p.parameterDefinition.id === genomeViewerContext.regionParam.parameterId,
  );
  if (regionParam) {
    const regionFieldProps: WorkflowActivityParameterFieldProps = buildFieldProps(regionParam);
    const regionValue = Just({
      contigId: region.contig?.id,
      range:
        !Number.isNaN(region.range?.start) && !Number.isNaN(region.range?.stop)
          ? { start: region.range?.start, stop: region.range?.stop }
          : undefined,
    });

    const storedRegionParam: WorkflowParameter = buildWorkflowParameter(
      genomeViewerContext.regionParam.instanceId,
      genomeViewerContext.regionParam.parameterId,
      "RegionParam",
      regionValue,
      true,
      generateValidationSchema(regionFieldProps),
    );

    regionParam.workflowParameter = storedRegionParam;
  }

  return newExecutionState;
}

async function initNfPlaybookExecutionFormStateFromProcessor(
  p2ExtendedConfig: WorkflowExtendedConfig,
  sampleIds: number[],
): Promise<Partial<INFPlaybookExecutionState>> {
  const p2Params: ActivityInstanceExtendedParameterWithActivity[] = await flattenStagedActivityExtendedInstances(
    p2ExtendedConfig.activities,
    true,
    false,
    // sampleIds are passed on when the Playbook is launched from a sample selection
    sampleIds,
  );
  const executionState: Partial<INFPlaybookExecutionState> = {
    p2Params: p2Params.sort((a, b) => ((a.sortOrder || 0) > (b.sortOrder || 0) ? 1 : -1)),
  };
  return executionState;
}

function initNfPlaybookExecutionFormStateFromEvent(event: PlaybookEvent): Partial<INFPlaybookExecutionState> {
  // figure out the experiment level attributes corresponding to Workflow fields
  const workflowValues: Dictionary<NfParamValue[]> = {};
  let contentUnavailableWarning: string;

  for (const input of event.inputs) {
    // the input might be a resource...
    const experimentResourceValues: NfParamValue[] = [];

    if (input.resources?.length) {
      for (const resource of input.resources) {
        if (resource.file && !isFileAccessible(resource.file)) {
          contentUnavailableWarning = selMessage({
            id: "playbook.execution.warning.event.fileResourcesUnavailable",
          });
        }
        if (resource.organism && resource.organism.hidden) {
          contentUnavailableWarning = selMessage({
            id: "playbook.execution.warning.event.genomeResourcesUnavailable",
          });
        }
        const nfParamValue: NfParamValue = createNfWorkflowParamValueFromEventInput(resource);
        if (nfParamValue) {
          experimentResourceValues.push(nfParamValue);
        }
      }
    } else if (input.inputValues?.length) {
      // ..or a simple input (every workflow field that is not considered a resource)
      const firstInputValue = input.inputValues[0]; // never multiple values
      const nfParamValue: NfParamValue = createNfParamValue({
        // the type of the original input value is not preserved so we have to infer
        textValue: firstInputValue,
        booleanValue: Boolean(firstInputValue),
        numberValue: firstInputValue ? Number(firstInputValue) : null,
      });
      experimentResourceValues.push(nfParamValue);
    }
    // build the workflow entry value
    workflowValues[input.workflowField] = experimentResourceValues;
  }
  return { workflowValues: workflowValues, contentUnavailableWarning: contentUnavailableWarning };
}

export function submitNfPlaybook() {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient) => {
    const state: IAppState = getState();
    const playbookExecution: INFPlaybookExecutionState = state.nfPlaybookExecution;
    const sampleEditingState: ISampleEditingState = getState().sampleEditingState;
    const playbookType: number = playbookExecution.playbook.type.id;

    const workflowParamInputValues: InputNfParamInput[] = playbookExecution.playbook.nfConfig?.workflowParameters
      // .filter((workflowParam) => !workflowParam.hidden && !isHiddenNfParamType(workflowParam.type))
      .map((workflowParam) => {
        const value = playbookExecution.workflowValues[workflowParam.identifier];
        const paramInput: InputNfParamInput = extractNfWorkflowParamInputValues(workflowParam, value);
        return paramInput;
      });

    // in case of report based Playbooks, the related table query specification is mapped as a classical input parameter
    if (
      playbookExecution.playbook.type.class === PLAYBOOK_CLASS.REPORT ||
      playbookExecution.playbook.type.class === PLAYBOOK_CLASS.ALIGNMENT
    ) {
      const reportTableQuerySpecInput: InputNfParamInput = {
        identifier: "playbook_report_specification",
        type: { type: NfParamTypeEnum.TableQuerySpec },
        values: [{ tableQuerySpec: playbookExecution.reportTableQuerySpec }],
      };
      workflowParamInputValues.push(reportTableQuerySpecInput);
    }

    const isSampleAnalysis: boolean = playbookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES;

    let sampleEditingInput: InputEditSamplesInput;
    // currently we only support sample editing in NF playbooks;
    // see playbook-execution for full logic required for sample registration
    if (isSampleAnalysis) {
      sampleEditingInput = extractSamplesEditingInput(sampleEditingState);
    }

    let activities: InputPlaybookActivityInput[];
    if (playbookType === PLAYBOOK_TYPE.PROCESSOR) {
      activities = playbookExecution.playbook.p2Config?.workflow?.extendedConfig.activities.map((activity) => {
        // collect all parameters and their values for that activity
        const parameters = playbookExecution.p2Params
          .filter((param) => param.activity.identifier === activity.identifier)
          .map((param) => param.workflowParameter)
          .map((workflowParam) => {
            return {
              identifier: workflowParam?.parameterId,
              values: extractValue(workflowParam),
            } satisfies InputActivityBasicParameterInput;
          });
        return { identifier: activity.identifier, parameters: parameters } satisfies InputPlaybookActivityInput;
      });
    }

    const eventInputData: InputPlaybookEventInput = {
      alias: playbookExecution.eventName,
      note: playbookExecution.eventDescription,
      playbookId: playbookExecution.playbook.id,
      projectId: playbookExecution.project.id,
      activities: activities,
    };

    dispatch({
      type: EXECUTE_ANALYSIS_REQUEST,
    });
    dispatch(auditPlaybookSubmit(playbookExecution.playbook as unknown as Playbook));

    return webApiClient
      .submitNextflowPlaybook(eventInputData, workflowParamInputValues, sampleEditingInput)
      .then((result: boolean) => {
        if (result) {
          dispatch({
            type: EXECUTE_ANALYSIS_SUCCESS,
          });

          notify.info(
            {
              title: selMessage({
                id: "playbook.submission.successTitle",
                values: { isSampleAnalysis: false },
              }),
              children: SelMessage({
                id: "playbook.submission.successMessage",
                values: {
                  playbookName: playbookExecution.playbook.name,
                },
              }),
            },
            { autoClose: 10_000 },
          );

          dispatch(refreshEventsListing());
        } else {
          throw new Error("The worklfow could not be submitted");
        }
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: EXECUTE_ANALYSIS_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function checkSingleProcessorActivityParameter(
  nodeId: string,
  parameterId: string,
  errors: WorkflowErrorParameter[],
) {
  return (_dispatch, getState: () => IAppState, _webApiClient: WebApiClient): WorkflowErrorParameter[] => {
    const state: IAppState = getState();
    const p2Parameters = state.nfPlaybookExecution.p2Params.map((p) => p.workflowParameter);
    const parameter = p2Parameters.find((param) => {
      return param?.nodeId === nodeId && param?.parameterId === parameterId;
    });
    const existingErrorIndex = errors.findIndex(
      (error) => error.nodeId === parameter?.nodeId && error.parameterId === parameter.parameterId,
    );
    const result = parameter?.schema?.safeParse(parameter?.values.extractNullable());
    return match(result)
      .with({ success: false, error: P.select() }, (error) => {
        const newError = {
          type: "parameter",
          nodeId: parameter?.nodeId,
          parameterId: parameter?.parameterId,
          message: error.issues[0].message,
          code: error.issues[0].params?.code ?? error.issues[0].code,
        } satisfies WorkflowErrorParameter;
        return existingErrorIndex < 0
          ? [...errors, newError]
          : errors.map((error, index) => (index === existingErrorIndex ? newError : error));
      })
      .otherwise(() => {
        // Clean error if any
        if (existingErrorIndex >= 0) {
          return errors.filter((_error, index) => index !== existingErrorIndex);
        }
        return errors;
      });
  };
}

/** ******************** Utils ***************************/

export const getNfNGSFileAttributes = (playbook: IConfiguredPlaybook, sampleAttributes: Attribute[]): Attribute[] => {
  const workflowReadsAttributes: NfSampleAttribute[] = playbook.nfConfig?.trackerParameters.sampleAttributes.filter(
    (a) => !!a.workflowParam,
  );

  const ngsDataAttributes: Attribute[] = [];

  workflowReadsAttributes.forEach((a) => {
    const attribute = sampleAttributes.find((sampleAttribute) => sampleAttribute.id === a.attributeId);
    if (isNgsDataFileAttribute(attribute)) {
      ngsDataAttributes.push(attribute);
    }
  });

  return ngsDataAttributes;
};
